import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { FileEnums } from "models/enums";
import { FileProfileInfo, User } from "models/propTypes/userProfilePropTypes";
import { WintSuperkollAPIModel as ApiModels } from "wint-api-client/dist/types";
import { DateConstants } from "utils/constants";
import { SalaryFormTypes } from "models/formTypes";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { NestedTable, useFilterContext, useSearchContext } from "components/common";
import SalarySpecificationsListView from "./salarySpecificationsListView";
import { useSalaryDeviationsService } from "serviceHooks";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import { pascalize } from "humps";
import cssExports from "theme/styles/main.module.scss";

const PAYSLIP_PAGE_SIZE = 1000;

// Row type covering both the employee level (depth 0) and the payslip
// level (depth 1). Modeled as a single shape with optional fields so
// NestedTable's `keyof RowType`-based `properties` accepts every key
// we render, while still being assignable from either API model at
// runtime.
type SalarySpecificationRow = {
  id: number;
  personName?: string;
  fileId?: number;
  fileName?: string;
  monthDb?: number;
  children?: SalarySpecificationRow[];
};

const SalarySpecificationListView: React.FC = () => {
  const salaryDeviationsService = useSalaryDeviationsService();
  const { filter } = useFilterContext<SalaryFormTypes.SalarySpecificationFilter>();
  const searchContext = useSearchContext();
  const filterYear = filter?.year;
  const filterPersonId = filter?.personId;
  const [users, setUsers] = React.useState<ApiModels.SalarySpecificationsUser[] | undefined>(undefined);
  const [rowsWithChildren, setRowsWithChildren] = React.useState<SalarySpecificationRow[] | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    salaryDeviationsService
      .getSalarySpecificationUsers()
      .then((res) => {
        if (cancelled) return;
        setUsers(res.data);
      })
      .catch(() => {
        // Axios interceptor surfaces the error toast; default to an empty
        // list so the UI exits the loading state instead of hanging.
        if (cancelled) return;
        setUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!users) return;
    if (users.length === 0) {
      setRowsWithChildren([]);
      return;
    }
    if (users.length === 1) return; // single-user view renders flat list, no preload needed
    setRowsWithChildren(undefined);
    let cancelled = false;
    salaryDeviationsService
      .getSalarySpecificationsForUser({
        page: 0,
        numPerPage: PAYSLIP_PAGE_SIZE,
        orderByProperty: pascalize("monthDb"),
        orderByDescending: true,
        thenByDescending: false,
        isAutoPaginating: true,
        year: filterYear,
      })
      .then((response) => {
        if (cancelled) return;
        const payslipsByName = new Map<string, SalarySpecificationRow[]>();
        for (const payslip of response.data.items ?? []) {
          const name = payslip.personName ?? "";
          const existing = payslipsByName.get(name);
          if (existing) {
            existing.push(payslip);
          } else {
            payslipsByName.set(name, [payslip]);
          }
        }
        setRowsWithChildren(
          users
            .map((u) => ({
              ...u,
              children: payslipsByName.get(u.personName ?? "") ?? [],
            }))
            .filter((row) => (row.children?.length ?? 0) > 0)
        );
      })
      .catch(() => {
        // Axios interceptor surfaces the error toast; render an empty
        // table so the UI exits the skeleton state without dangling user
        // rows that have nothing to expand into.
        if (cancelled) return;
        setRowsWithChildren([]);
      });
    return () => {
      cancelled = true;
    };
  }, [users, filterYear]);

  // Every employee row for the selected year is already in memory, so the
  // toolbar search and the employee filter narrow the loaded rows rather than
  // going back to the API. Children are kept intact: narrowing is about finding
  // the right person, not about hiding any of their payslips.
  const visibleRows = React.useMemo(() => {
    const searchTerm = searchContext?.searchText?.trim().toLowerCase();
    let rows = rowsWithChildren ?? [];
    if (filterPersonId) rows = rows.filter((row) => row.id === filterPersonId);
    if (searchTerm) rows = rows.filter((row) => (row.personName ?? "").toLowerCase().includes(searchTerm));
    return rows;
  }, [rowsWithChildren, filterPersonId, searchContext?.searchText]);

  const onRowClick = (row: SalarySpecificationRow, _index: number, depth?: number) => {
    if (depth === 1) {
      salaryDeviationsService.downloadSalarySpecification(row.id);
    }
  };

  if (users && users.length === 1) {
    return (
      <Grid container item>
        <SalarySpecificationsListView useExtendedFilter salarySpecificationUser={users[0]} year={filterYear} />
      </Grid>
    );
  }

  return (
    <Grid container item>
      {/* NestedTable keeps its page in local state and never resets it when rows change, so a
          narrowing search from page 3 would slice past the end and leave an empty table with the
          pager hidden. Remounting on every change to what narrows the rows puts it back on page 1. */}
      <NestedTable<SalarySpecificationRow, any>
        key={`${filterYear ?? ""}|${filterPersonId ?? ""}|${searchContext?.searchText ?? ""}`}
        properties={{
          fileName: {
            description: TranslationKeys.Common.Name,
            formatter: (row: SalarySpecificationRow, _index: number, depth?: number) =>
              depth === 0
                ? new User(row.id, row.personName, false, false, true)
                : new FileProfileInfo(row.fileName ?? "", FileEnums.ContentTypes.ApplicationPDF.toString()),
            noWrap: true,
          },
          monthDb: {
            description: TranslationKeys.Common.Period,
            formatter: (row: SalarySpecificationRow, _index: number, depth?: number) =>
              depth === 0 || !row.monthDb
                ? ""
                : LocalizationHelperInstance.getFormattedDate(
                    DateHelper.getDateFromYearMonth(row.monthDb),
                    DateConstants.MonthYearShortConfig
                  ),
            noWrap: true,
            width: 100,
            classification: "date",
            className: cssExports.fourth,
          },
        }}
        rows={visibleRows}
        childrenKey="children"
        rowClickHandler={onRowClick}
        loading={rowsWithChildren === undefined}
        maxRowCount={10}
        hidePaginationIfSinglePage
        pagination="advanced"
      />
    </Grid>
  );
};

export default SalarySpecificationListView;
